<template>
    <div :class="[$style['mds-layout-grid__col'], classObject]" :data-mds-version="version">
        <slot></slot>
    </div>
</template>

<script>
    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsCol',
        data() {
            return {
                version: `${name}-${version}`,
            };
        },
        props: {
            alignVertical: {
                type: String,
                validator(value) {
                    return ['flex-start', 'center', 'flex-end'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `flex-start`, `center`, `flex-end`.',
                    description: 'Sets the `align-self` behavior for column content.',
                },
            },
            cols: {
                type: [Number, String],
                validator(value) {
                    return value === 'auto' || (value > 0 && value < 13);
                },
                docs: {
                    validation: 'Must be either a number between `1` and `12` or `auto`.',
                    description: 'Sets the number of columns, out of 12, for the column to span.',
                },
            },
            colsAtS: {
                type: [Number, String],
                validator(value) {
                    return value === 'auto' || (value > 0 && value < 13);
                },
                docs: {
                    validation: 'Must be either a number between `1` and `12` or `auto`.',
                    description:
                        'Sets the number of columns, out of 12, for the column to span at the small breakpoint (>600px).',
                },
            },
            colsAtM: {
                type: [Number, String],
                validator(value) {
                    return value === 'auto' || (value > 0 && value < 13);
                },
                docs: {
                    validation: 'Must be either a number between `1` and `12` or `auto`.',
                    description:
                        'Sets the number of columns, out of 12, for the column to span at the medium breakpoint (>768px).',
                },
            },
            colsAtL: {
                type: [Number, String],
                validator(value) {
                    return value === 'auto' || (value > 0 && value < 13);
                },
                docs: {
                    validation: 'Must be either a number between `1` and `12` or `auto`.',
                    description:
                        'Sets the number of columns, out of 12, for the column to span at the large breakpoint (>1000px).',
                },
            },
            colsAtXl: {
                type: [Number, String],
                validator(value) {
                    return value === 'auto' || (value > 0 && value < 13);
                },
                docs: {
                    validation: 'Must be either a number between `1` and `12` or `auto`.',
                    description:
                        'Sets the number of columns, out of 12, for the column to span at the extra-large breakpoint (>1200px).',
                },
            },
            pull: {
                type: Number,
                validator(value) {
                    return value >= 0 && value < 12;
                },
                docs: {
                    validation: 'Must be number between `1` and `11`.',
                    description: 'Applies right margin in column increments.',
                },
            },
            pullAtS: {
                type: Number,
                validator(value) {
                    return value >= 0 && value < 12;
                },
                docs: {
                    validation: 'Must be number between `1` and `11`.',
                    description: 'Applies right margin in column increments at the small breakpoint (>600px).',
                },
            },
            pullAtM: {
                type: Number,
                validator(value) {
                    return value >= 0 && value < 12;
                },
                docs: {
                    validation: 'Must be number between `1` and `11`.',
                    description: 'Applies right margin in column increments at the medium breakpoint (>768px).',
                },
            },
            pullAtL: {
                type: Number,
                validator(value) {
                    return value >= 0 && value < 12;
                },
                docs: {
                    validation: 'Must be number between `1` and `11`.',
                    description: 'Applies right margin in column increments at the large breakpoint (>1000px).',
                },
            },
            pullAtXl: {
                type: Number,
                validator(value) {
                    return value >= 0 && value < 12;
                },
                docs: {
                    validation: 'Must be number between `1` and `11`.',
                    description: 'Applies right margin in column increments at the extra-large breakpoint (>1200px).',
                },
            },
            push: {
                type: Number,
                validator(value) {
                    return value >= 0 && value < 12;
                },
                docs: {
                    validation: 'Must be number between `1` and `11`.',
                    description: 'Applies left margin in column increments.',
                },
            },
            pushAtS: {
                type: Number,
                validator(value) {
                    return value >= 0 && value < 12;
                },
                docs: {
                    validation: 'Must be number between `1` and `11`.',
                    description: 'Applies left margin in column increments at the small breakpoint (>600px).',
                },
            },
            pushAtM: {
                type: Number,
                validator(value) {
                    return value >= 0 && value < 12;
                },
                docs: {
                    validation: 'Must be number between `1` and `11`.',
                    description: 'Applies left margin in column increments at the medium breakpoint (>768px).',
                },
            },
            pushAtL: {
                type: Number,
                validator(value) {
                    return value >= 0 && value < 12;
                },
                docs: {
                    validation: 'Must be number between `1` and `11`.',
                    description: 'Applies left margin in column increments at the large breakpoint (>1000px).',
                },
            },
            pushAtXl: {
                type: Number,
                validator(value) {
                    return value >= 0 && value < 12;
                },
                docs: {
                    validation: 'Must be number between `1` and `11`.',
                    description: 'Applies left margin in column increments at the extra-large breakpoint (>1200px).',
                },
            },
            squeeze: {
                type: Number,
                validator(value) {
                    return value >= 0 && value < 6;
                },
                docs: {
                    validation: 'Must be number between `1` and `5`.',
                    description: 'Applies left and right margin equally in column increments.',
                },
            },
            squeezeAtS: {
                type: Number,
                validator(value) {
                    return value >= 0 && value < 6;
                },
                docs: {
                    validation: 'Must be number between `1` and `5`.',
                    description:
                        'Applies left and right margin equally in column increments at the small breakpoint (>600px).',
                },
            },
            squeezeAtM: {
                type: Number,
                validator(value) {
                    return value >= 0 && value < 6;
                },
                docs: {
                    validation: 'Must be number between `1` and `5`.',
                    description:
                        'Applies left and right margin equally in column increments at the medium breakpoint (>768px).',
                },
            },
            squeezeAtL: {
                type: Number,
                validator(value) {
                    return value >= 0 && value < 6;
                },
                docs: {
                    validation: 'Must be number between `1` and `5`.',
                    description:
                        'Applies left and right margin equally in column increments at the large breakpoint (>1000px).',
                },
            },
            squeezeAtXl: {
                type: Number,
                validator(value) {
                    return value >= 0 && value < 6;
                },
                docs: {
                    validation: 'Must be number between `1` and `5`.',
                    description:
                        'Applies left and right margin equally in column increments at the extra-large breakpoint (>1200px).',
                },
            },
        },
        inject: ['parent'],
        computed: {
            classObject() {
                const obj = {};
                const baseClassName = 'mds-layout-grid__col';
                obj[this.$style[`${baseClassName}--align-self-${this.alignVertical}`]] = this.alignVertical;
                obj[this.$style[`${baseClassName}--${this.cols}`]] = this.cols;
                obj[this.$style[`${baseClassName}--${this.colsAtS}-at-600`]] = this.colsAtS;
                obj[this.$style[`${baseClassName}--${this.colsAtM}-at-768`]] = this.colsAtM;
                obj[this.$style[`${baseClassName}--${this.colsAtL}-at-1000`]] = this.colsAtL;
                obj[this.$style[`${baseClassName}--${this.colsAtXl}-at-1200`]] = this.colsAtXl;
                obj[this.$style[`${baseClassName}--pull-${this.pull}`]] = this.pull;
                obj[this.$style[`${baseClassName}--pull-${this.pullAtS}-at-600`]] = this.pullAtS;
                obj[this.$style[`${baseClassName}--pull-${this.pullAtM}-at-768`]] = this.pullAtM;
                obj[this.$style[`${baseClassName}--pull-${this.pullAtL}-at-1000`]] = this.pullAtL;
                obj[this.$style[`${baseClassName}--pull-${this.pullAtXl}-at-1200`]] = this.pullAtXl;
                obj[this.$style[`${baseClassName}--push-${this.push}`]] = this.push;
                obj[this.$style[`${baseClassName}--push-${this.pushAtS}-at-600`]] = this.pushAtS;
                obj[this.$style[`${baseClassName}--push-${this.pushAtM}-at-768`]] = this.pushAtM;
                obj[this.$style[`${baseClassName}--push-${this.pushAtL}-at-1000`]] = this.pushAtL;
                obj[this.$style[`${baseClassName}--push-${this.pushAtXl}-at-1200`]] = this.pushAtXl;
                obj[this.$style[`${baseClassName}--squeeze-${this.squeeze}`]] = this.squeeze;
                obj[this.$style[`${baseClassName}--squeeze-${this.squeezeAtS}-at-600`]] = this.squeezeAtS;
                obj[this.$style[`${baseClassName}--squeeze-${this.squeezeAtM}-at-768`]] = this.squeezeAtM;
                obj[this.$style[`${baseClassName}--squeeze-${this.squeezeAtL}-at-1000`]] = this.squeezeAtL;
                obj[this.$style[`${baseClassName}--squeeze-${this.squeezeAtXl}-at-1200`]] = this.squeezeAtXl;
                obj[this.$style['mds-layout-grid__col--zero-padding']] = this.parent.gutters === 'none';
                return obj;
            },
        },
    };
</script>

<style lang="scss" module>
    @import './layout-grid.scss';
</style>
